import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  type GetCartResult,
  getClaimPollingQueryFn,
  clearLabelFoundCache,
} from '../../graphql/query/getClaim';
import { QUERIES } from '../../utils/constants';
import { pdxEvent } from '../../utils/noflake';

export type PollingStatus =
  | 'polling'
  | 'success'
  | 'timeout'
  | 'error'
  | 'disabled';

type UseSubmittedRequestPollingProps = {
  requestId?: string | null | undefined;
  enabled?: boolean;
  timeoutSeconds?: number;
  pollIntervalMs?: number;
};

type ShipmentData = {
  labelUrl: string | null;
  qrCodeUrl: string | null;
};

type UseSubmittedRequestPollingReturn = {
  status: PollingStatus;
  shipmentData: ShipmentData;
  error: Error | null;
};

const DEFAULT_TIMEOUT_SEC = 60;
const DEFAULT_POLL_INTERVAL_MS = 2000;

export const useLabelPolling = ({
  requestId,
  enabled = true,
  timeoutSeconds = DEFAULT_TIMEOUT_SEC,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
}: UseSubmittedRequestPollingProps): UseSubmittedRequestPollingReturn => {
  const [pollingStatus, setPollingStatus] = useState<PollingStatus>(
    enabled ? 'polling' : 'disabled',
  );
  const [shipmentData, setShipmentData] = useState<ShipmentData>({
    labelUrl: null,
    qrCodeUrl: null,
  });
  const startTimeRef = useRef<number>(Date.now());

  const prevRequestId = useRef(requestId);

  const isQueryEnabled =
    enabled && requestId != null && pollingStatus === 'polling';

  const { data: submittedRequestsData, error: submittedRequestsError } =
    useQuery<GetCartResult, Error>({
      queryKey: [QUERIES.GET_CLAIM, requestId],
      queryFn: () => getClaimPollingQueryFn(requestId!),
      enabled: Boolean(isQueryEnabled),
      refetchInterval: pollIntervalMs,
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    });

  const handlePollingUpdate = useCallback(() => {
    if (!enabled) return setPollingStatus('disabled');
    if (submittedRequestsError) {
      return setPollingStatus('error');
    }
    if (pollingStatus !== 'polling') return;

    const shipment = submittedRequestsData?.claim?.shipments?.[0];
    if (shipment && (shipment.labelUrl || shipment.qrCodeUrl)) {
      setShipmentData({
        labelUrl: shipment.labelUrl ?? null,
        qrCodeUrl: shipment.qrCodeUrl ?? null,
      });
      return setPollingStatus('success');
    }

    const elapsedTimeSec = (Date.now() - startTimeRef.current) / 1000;
    if (elapsedTimeSec >= timeoutSeconds) {
      pdxEvent({
        event_category: 'get_data',
        event_type: 'get_label_polling_timeout',
        event_value: 'label',
      });
      setPollingStatus('timeout');
      setShipmentData({ labelUrl: null, qrCodeUrl: null });
      return;
    }
  }, [
    enabled,
    submittedRequestsData,
    submittedRequestsError,
    pollingStatus,
    timeoutSeconds,
  ]);

  useEffect(() => {
    if (!isQueryEnabled) return;

    const interval = setInterval(() => {
      handlePollingUpdate();
    }, pollIntervalMs);

    return () => clearInterval(interval);
  }, [isQueryEnabled, handlePollingUpdate, pollIntervalMs]);

  useEffect(() => {
    if (enabled && prevRequestId.current !== requestId) {
      startTimeRef.current = Date.now();
      setPollingStatus('polling');
      prevRequestId.current = requestId;
      // Clear the cache for the new request to ensure fresh polling
      if (requestId) {
        clearLabelFoundCache(requestId);
      }
    } else if (!enabled) {
      setPollingStatus('disabled');
      setShipmentData({ labelUrl: null, qrCodeUrl: null });
    }
  }, [requestId, enabled]);

  return {
    status: pollingStatus,
    shipmentData,
    error: submittedRequestsError || null,
  };
};
